<!--
  SubstrateBuckets -- SuperAdmin ops-review queue for the
  vendor_color_hex.substrate_bucket column.

  Decoration Model Refactor -- Phase P0.
  Card: Boulders #163 (https://trello.com/c/S5kN5poU)

  Why this page exists
  ====================
  The luminance heuristic in src/services/substrate_bucket.py auto-
  classifies obvious cases (white, black, navy, beige). Brand-color
  edge cases (royal blue, safety green, mid-heathers) and swatch-only
  rows defer to ops via NULL substrate_bucket. This page is where ops
  triages those NULL rows so the Decoration approval model can use one
  approval per artwork-per-substrate-bucket.

  IA
  ==
  Two tabs (Unclassified | Classified) backed by the same endpoint's
  ``status`` query param. Each row shows:

    * Vendor + color SKU
    * Hex preview (swatch or color block)
    * Proposed bucket (the heuristic's preview)
    * Confirm / override actions (per-row PATCH)
    * Bulk-assign affordance (post the selected ids to bulk-assign)

  Endpoints
  =========
  * GET   /api/admin/vendor-color-hex/substrate-buckets?status=...
  * PATCH /api/admin/vendor-color-hex/<id>/substrate-bucket
  * POST  /api/admin/vendor-color-hex/substrate-buckets/bulk-assign

  Mounted at /admin/vendor-color-hex/substrate-buckets via
  router/groups/superadmin.js. Gated on superadmin.settings (matches
  the vendor_color_hex write tier).

  data-testid prefix: substrate-buckets-*
-->
<template>
    <section
        class="substrate-buckets"
        data-testid="substrate-buckets-root"
    >
        <header class="substrate-buckets__header">
            <h2 class="substrate-buckets__title">
                Substrate buckets review
            </h2>
            <span
                class="substrate-buckets__live"
                data-testid="substrate-buckets-live"
            >
                Live
            </span>
        </header>

        <p class="substrate-buckets__lede">
            One Decoration approval covers all light AND all dark
            variants of an artwork. Ops classifies each vendor color
            into the substrate bucket the decorator prints onto. The
            luminance heuristic auto-classifies obvious cases; rows on
            this page need a human call.
        </p>

        <nav
            class="substrate-buckets__tabs"
            data-testid="substrate-buckets-tabs"
        >
            <button
                type="button"
                class="substrate-buckets__tab"
                :class="{ 'substrate-buckets__tab--active': status === 'unclassified' }"
                data-testid="substrate-buckets-tab-unclassified"
                @click="onTabChange('unclassified')"
            >
                Unclassified
                <span class="substrate-buckets__count">
                    ({{ counts.unclassified }})
                </span>
            </button>
            <button
                type="button"
                class="substrate-buckets__tab"
                :class="{ 'substrate-buckets__tab--active': status === 'classified' }"
                data-testid="substrate-buckets-tab-classified"
                @click="onTabChange('classified')"
            >
                Classified
                <span class="substrate-buckets__count">
                    ({{ counts.classified }})
                </span>
            </button>
        </nav>

        <div
            v-if="loading"
            data-testid="substrate-buckets-loading"
        >
            Loading...
        </div>

        <div
            v-else-if="!rows.length"
            class="substrate-buckets__empty"
            data-testid="substrate-buckets-empty"
        >
            <span v-if="status === 'unclassified'">
                No rows awaiting review. The queue is clear.
            </span>
            <span v-else>
                No classified rows yet.
            </span>
        </div>

        <template v-else>
            <div
                v-if="status === 'unclassified'"
                class="substrate-buckets__bulk"
                data-testid="substrate-buckets-bulk"
            >
                <span class="substrate-buckets__bulk-label">
                    Selected: {{ selectedIds.length }}
                </span>
                <button
                    type="button"
                    class="tf-btn tf-btn--ghost"
                    :disabled="!selectedIds.length || saving"
                    data-testid="substrate-buckets-bulk-light"
                    @click="bulkAssign('light')"
                >
                    Assign all to light
                </button>
                <button
                    type="button"
                    class="tf-btn tf-btn--ghost"
                    :disabled="!selectedIds.length || saving"
                    data-testid="substrate-buckets-bulk-dark"
                    @click="bulkAssign('dark')"
                >
                    Assign all to dark
                </button>
            </div>

            <table
                class="substrate-buckets__table"
                data-testid="substrate-buckets-table"
            >
                <thead>
                    <tr>
                        <th v-if="status === 'unclassified'"></th>
                        <th>Vendor</th>
                        <th>Color SKU</th>
                        <th>Swatch</th>
                        <th>Hex / L</th>
                        <th>Proposed</th>
                        <th>Current</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in rows"
                        :key="row.id"
                        data-testid="substrate-buckets-row"
                    >
                        <td v-if="status === 'unclassified'">
                            <input
                                type="checkbox"
                                :checked="selectedIds.includes(row.id)"
                                :data-testid="`substrate-buckets-select-${row.id}`"
                                @change="toggleSelected(row.id)"
                            />
                        </td>
                        <td>{{ row.vendor_name }}</td>
                        <td>{{ row.color_sku }}</td>
                        <td>
                            <img
                                v-if="row.swatch_url"
                                :src="row.swatch_url"
                                :alt="row.color_sku + ' swatch'"
                                class="substrate-buckets__swatch-img"
                            />
                            <span
                                v-else-if="row.hex_value"
                                class="substrate-buckets__swatch-chip"
                                :style="{ backgroundColor: row.hex_value }"
                                :title="row.hex_value"
                            ></span>
                            <span v-else>--</span>
                        </td>
                        <td>
                            <span v-if="row.hex_value">
                                {{ row.hex_value }}
                                <small v-if="row.luminance !== null">
                                    L={{ row.luminance.toFixed(2) }}
                                </small>
                            </span>
                            <span v-else>--</span>
                        </td>
                        <td>
                            <span
                                class="substrate-buckets__proposed"
                                :data-testid="`substrate-buckets-proposed-${row.id}`"
                            >
                                {{ row.proposed_substrate_bucket || 'defer' }}
                            </span>
                        </td>
                        <td>
                            <span
                                :data-testid="`substrate-buckets-current-${row.id}`"
                            >
                                {{ row.substrate_bucket || 'unclassified' }}
                            </span>
                        </td>
                        <td>
                            <button
                                v-if="row.substrate_bucket !== 'light'"
                                type="button"
                                class="tf-btn tf-btn--ghost tf-btn--sm"
                                :disabled="saving"
                                :data-testid="`substrate-buckets-set-light-${row.id}`"
                                @click="setBucket(row.id, 'light')"
                            >
                                Light
                            </button>
                            <button
                                v-if="row.substrate_bucket !== 'dark'"
                                type="button"
                                class="tf-btn tf-btn--ghost tf-btn--sm"
                                :disabled="saving"
                                :data-testid="`substrate-buckets-set-dark-${row.id}`"
                                @click="setBucket(row.id, 'dark')"
                            >
                                Dark
                            </button>
                            <button
                                v-if="row.substrate_bucket !== null"
                                type="button"
                                class="tf-btn tf-btn--ghost tf-btn--sm"
                                :disabled="saving"
                                :data-testid="`substrate-buckets-clear-${row.id}`"
                                @click="setBucket(row.id, null)"
                            >
                                Reset
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </template>

        <p
            v-if="errorMsg"
            class="substrate-buckets__error"
            data-testid="substrate-buckets-error"
        >
            {{ errorMsg }}
        </p>
    </section>
</template>

<script>
import axios from "@/axios";

export default {
    name: "SubstrateBuckets",
    data() {
        return {
            status: "unclassified",
            rows: [],
            counts: { unclassified: 0, classified: 0, total: 0 },
            selectedIds: [],
            loading: false,
            saving: false,
            errorMsg: "",
        };
    },
    mounted() {
        this.fetch();
    },
    methods: {
        async fetch() {
            this.loading = true;
            this.errorMsg = "";
            try {
                const resp = await axios.get(
                    `/api/admin/vendor-color-hex/substrate-buckets?status=${this.status}&limit=500`
                );
                this.rows = resp.data.rows || [];
                this.counts = resp.data.counts || this.counts;
                this.selectedIds = [];
            } catch (err) {
                this.errorMsg =
                    err.response?.data?.error || "Failed to load rows.";
            } finally {
                this.loading = false;
            }
        },
        onTabChange(next) {
            if (next === this.status) return;
            this.status = next;
            this.fetch();
        },
        toggleSelected(id) {
            const idx = this.selectedIds.indexOf(id);
            if (idx === -1) {
                this.selectedIds.push(id);
            } else {
                this.selectedIds.splice(idx, 1);
            }
        },
        async setBucket(id, bucket) {
            this.saving = true;
            this.errorMsg = "";
            try {
                await axios.patch(
                    `/api/admin/vendor-color-hex/${id}/substrate-bucket`,
                    { substrate_bucket: bucket }
                );
                await this.fetch();
            } catch (err) {
                this.errorMsg =
                    err.response?.data?.error || "Failed to update row.";
            } finally {
                this.saving = false;
            }
        },
        async bulkAssign(bucket) {
            if (!this.selectedIds.length) return;
            this.saving = true;
            this.errorMsg = "";
            try {
                await axios.post(
                    "/api/admin/vendor-color-hex/substrate-buckets/bulk-assign",
                    {
                        ids: [...this.selectedIds],
                        substrate_bucket: bucket,
                    }
                );
                await this.fetch();
            } catch (err) {
                this.errorMsg =
                    err.response?.data?.error || "Bulk assign failed.";
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.substrate-buckets {
    padding: 1.5rem;
}
.substrate-buckets__header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
}
.substrate-buckets__title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
}
.substrate-buckets__live {
    font-size: 0.75rem;
    padding: 0.125rem 0.5rem;
    background: #10b981;
    color: white;
    border-radius: 999px;
}
.substrate-buckets__lede {
    color: #6b7280;
    max-width: 720px;
    margin-bottom: 1rem;
}
.substrate-buckets__tabs {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
    border-bottom: 1px solid #e5e7eb;
}
.substrate-buckets__tab {
    background: none;
    border: none;
    padding: 0.5rem 1rem;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    color: #6b7280;
}
.substrate-buckets__tab--active {
    color: #111827;
    border-bottom-color: #2563eb;
    font-weight: 600;
}
.substrate-buckets__count {
    color: var(--tf-fg-4);
    font-size: 0.875rem;
}
.substrate-buckets__bulk {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
}
.substrate-buckets__bulk-label {
    color: #6b7280;
    margin-right: 0.5rem;
}
.substrate-buckets__table {
    width: 100%;
    border-collapse: collapse;
}
.substrate-buckets__table th,
.substrate-buckets__table td {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
    font-size: 0.875rem;
}
.substrate-buckets__swatch-img {
    width: 32px;
    height: 32px;
    border-radius: 4px;
    object-fit: cover;
}
.substrate-buckets__swatch-chip {
    display: inline-block;
    width: 32px;
    height: 32px;
    border-radius: 4px;
    border: 1px solid #e5e7eb;
}
.substrate-buckets__proposed {
    text-transform: capitalize;
    color: #6b7280;
}
.substrate-buckets__empty {
    padding: 2rem;
    text-align: center;
    color: var(--tf-fg-4);
}
.substrate-buckets__error {
    color: #dc2626;
    margin-top: 0.75rem;
}
</style>
